import * as vp from 'vopay';

import { FunctionComponent, useEffect, useRef, useState, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import vopayApi from '../../../services/api.config';
import SubAccount from './SubAccount';
import usePartnerBearer from '../../../hooks/useApi/usePartnerBearer';
import AccountContext from '../AccountContext';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';
import { useAlertDialog } from '../../../providers/AlertDialog.provider';

import type { AccountsHandlers, Balances as Balance } from '../types';

type SubAccountContainerProps = {
  api: vp.Api;
  handlers: AccountsHandlers;
};

type FC = FunctionComponent<SubAccountContainerProps>;

const SubAccountContainer: FC = ({ api, handlers }: SubAccountContainerProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.SubAccount' });
  const { canAccessOnboardingTokenPanel } = useModuleFeatureFlag('accounts');
  const alertDialog = useAlertDialog();

  const query = new URLSearchParams(useLocation().search);
  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as vp.SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const { accountId } = useParams<{ accountId: string }>();
  const { details } = useContext(AccountContext);
  const partnerBearer = usePartnerBearer(details[accountId]?.PartnerAccountID ?? '');
  const [hasValidOnboardingUrl, setHasValidOnboardingUrl] = useState<boolean | null>(null);
  const [onBoardingLink, setOnboardingLink] = useState<string | null>(null);

  const sourceRef = useRef(axios.CancelToken.source());

  const fetchOnboardingUrl = async () => {
    if (!canAccessOnboardingTokenPanel) return;
    if (balance.isActive) return;

    setHasValidOnboardingUrl(null);
    sourceRef.current.cancel();
    sourceRef.current = axios.CancelToken.source();

    const partnerOnboardingUrlInfo = await api.fetchOnboardingAppUrl(
      { VopayAccountID: accountId },
      partnerBearer,
      sourceRef.current,
    );
    const { ErrorMessage, OnboardingURL } = partnerOnboardingUrlInfo;

    if (OnboardingURL) {
      setOnboardingLink(OnboardingURL);
      setHasValidOnboardingUrl(true);
    } else if (ErrorMessage) {
      setHasValidOnboardingUrl(false);
      alertDialog.error(t('OnboardingUrl.errorTitle'), ErrorMessage);
    }
  };

  useEffect(() => {
    // partnerBearer resolves asynchronously (empty on the first render), so wait for it
    // before fetching — and re-run once it resolves — otherwise the onboarding URL call
    // fires with an empty bearer and the section never loads (ID-12143).
    if (!partnerBearer) return undefined;

    if (balance.accountStatus === 'Application Pending') {
      void fetchOnboardingUrl();
    }
    return () => {
      sourceRef.current.cancel();
    };
  }, [partnerBearer, balance.accountStatus]);

  return (
    <SubAccount hasValidOnboardingUrl={hasValidOnboardingUrl} handlers={handlers} onBoardingLink={onBoardingLink} />
  );
};

export default vopayApi.withApiComponent<SubAccountContainerProps>(SubAccountContainer);
